import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import { getCheckoutStatus } from "@/lib/sponsor";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function SponsorSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();

  const [state, setState] = useState("polling"); // polling | success | failed | expired
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const data = await getCheckoutStatus(sessionId);
        setStatus(data);

        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        console.error(e);
        if (attempt >= MAX_ATTEMPTS) {
          setState("failed");
        } else {
          setTimeout(poll, POLL_MS);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem("halal_sponsor_session") || "null");
  } catch {
    stored = null;
  }

  return (
    <div
      data-testid="sponsor-success-page"
      className="min-h-screen w-full flex items-center justify-center bg-[#FDFBF7] p-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md rounded-3xl bg-white border border-[#E5E1D8] shadow-lg overflow-hidden"
      >
        <div
          className={`px-6 py-8 text-center ${
            state === "success"
              ? "bg-gradient-to-br from-[#FFF3EE] to-[#FFFBF0]"
              : "bg-[#FDFBF7]"
          }`}
        >
          {state === "polling" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[#D95D39] animate-spin" />
              <h1 className="mt-4 font-display text-2xl font-medium text-[#2A3B2C]">
                Confirming your payment…
              </h1>
              <p className="mt-1 text-sm text-[#647466]">
                Hold on a moment. Attempt {attempts} of {MAX_ATTEMPTS}.
              </p>
            </>
          )}

          {state === "success" && (
            <>
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 220 }}
                className="mx-auto w-16 h-16 rounded-full bg-[#D95D39] flex items-center justify-center"
              >
                <CheckCircle2 className="w-9 h-9 text-white" />
              </motion.div>
              <h1
                data-testid="sponsor-success-title"
                className="mt-4 font-display text-2xl font-medium text-[#2A3B2C]"
              >
                You're featured!
              </h1>
              <p className="mt-1 text-sm text-[#647466]">
                {stored?.restaurant_name || "Your restaurant"} now shines at the
                top of AlKoncierge.
              </p>
            </>
          )}

          {state === "expired" && (
            <>
              <AlertCircle className="w-10 h-10 mx-auto text-[#E1A624]" />
              <h1 className="mt-4 font-display text-2xl font-medium text-[#2A3B2C]">
                Payment session expired
              </h1>
              <p className="mt-1 text-sm text-[#647466]">
                No worries — try the boost flow again from your listing.
              </p>
            </>
          )}

          {state === "failed" && (
            <>
              <AlertCircle className="w-10 h-10 mx-auto text-[#D95D39]" />
              <h1 className="mt-4 font-display text-2xl font-medium text-[#2A3B2C]">
                We couldn't confirm yet
              </h1>
              <p className="mt-1 text-sm text-[#647466]">
                If the payment went through, your listing will be featured
                within a minute. Otherwise please try again.
              </p>
            </>
          )}
        </div>

        <div className="px-6 py-5 border-t border-[#E5E1D8] space-y-3">
          {state === "success" && status?.featured_until && (
            <div className="flex items-center gap-2 text-xs text-[#647466]">
              <Sparkles className="w-3.5 h-3.5 text-[#E1A624]" />
              Featured until{" "}
              <span className="font-semibold text-[#2A3B2C]">
                {new Date(status.featured_until).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          )}

          <button
            data-testid="back-to-map-btn"
            onClick={() => navigate("/")}
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] text-white text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to map
          </button>
        </div>
      </motion.div>
    </div>
  );
}
